import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { merchantApi } from '@/services/api/merchant'
import type { InviteStaffDto } from '@/types/merchant'

export const useMerchantStore = defineStore('merchant', () => {
  const staffToken = ref<string | null>(localStorage.getItem('staff_token'))
  const currentBranch = ref<any | null>(null)
  const dashboardData = ref<any | null>(null)
  const scannedMember = ref<any | null>(null)
  const staffList = ref<any[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isStaffAuthenticated = computed(() => !!staffToken.value)

  async function staffLogin(credentials: { email: string; passwordHash: string }) {
    isLoading.value = true
    error.value = null
    try {
      const res = await merchantApi.staffLogin(credentials)
      staffToken.value = res.access_token
      localStorage.setItem('staff_token', res.access_token)
      return res
    } catch (err: any) {
      error.value = err.message || 'Staff login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function staffLogout() {
    staffToken.value = null
    currentBranch.value = null
    dashboardData.value = null
    scannedMember.value = null
    localStorage.removeItem('staff_token')
  }

  async function fetchBrandDashboard(merchantId: string) {
    isLoading.value = true
    try {
      dashboardData.value = await merchantApi.getBrandDashboard(merchantId)
    } catch (err: any) {
      error.value = err.message
    } finally {
      isLoading.value = false
    }
  }

  async function scanMember(memberQrCode: string) {
    isLoading.value = true
    try {
      scannedMember.value = await merchantApi.scanMember(memberQrCode)
      return scannedMember.value
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchStaffList(merchantId: string) {
    isLoading.value = true
    error.value = null
    try {
      staffList.value = await merchantApi.listStaff(merchantId)
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch staff list'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function inviteStaff(merchantId: string, data: InviteStaffDto) {
    isLoading.value = true
    error.value = null
    try {
      const result = await merchantApi.inviteStaff(merchantId, data)
      await fetchStaffList(merchantId)
      return result
    } catch (err: any) {
      error.value = err.message || 'Failed to invite staff'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateStaff(merchantId: string, merchantUserId: string, data: any) {
    isLoading.value = true
    error.value = null
    try {
      const result = await merchantApi.updateStaff(merchantId, merchantUserId, data)
      await fetchStaffList(merchantId)
      return result
    } catch (err: any) {
      error.value = err.message || 'Failed to update staff'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function removeStaff(merchantId: string, merchantUserId: string) {
    isLoading.value = true
    error.value = null
    try {
      const result = await merchantApi.removeStaff(merchantId, merchantUserId)
      await fetchStaffList(merchantId)
      return result
    } catch (err: any) {
      error.value = err.message || 'Failed to remove staff'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    staffToken,
    currentBranch,
    dashboardData,
    scannedMember,
    staffList,
    isLoading,
    error,
    isStaffAuthenticated,
    staffLogin,
    staffLogout,
    fetchBrandDashboard,
    scanMember,
    fetchStaffList,
    inviteStaff,
    updateStaff,
    removeStaff,
  }
})
